import React, { useState } from "react";
import "./SegmentedPicker.css";
interface SegmentOption {
  label: string;
  value: string;
}

interface SegmentedPickerProps {
  /** Array of option objects with label and value */
  options: SegmentOption[];
  /** Initial selected value */
  initialSelected?: string;
  /** Current selected value (for controlled component) */
  selected?: string;
  /** Callback function when selection changes */
  onChange?: (value: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SegmentedPicker - A customizable segmented control component for selecting from mutually exclusive options
 * @returns Segmented picker component
 */
const SegmentedPicker = ({
  options,
  initialSelected,
  selected,
  onChange,
  className = "",
}: SegmentedPickerProps) => {
  const [internalSelected, setInternalSelected] = useState(
    initialSelected || options[0]?.value
  );

  // Use the selected prop if provided (controlled), otherwise use internal state (uncontrolled)
  const currentSelected = selected !== undefined ? selected : internalSelected;

  const handleSelect = (value: string) => {
    if (selected !== undefined) {
      // Controlled component - let parent handle state
      onChange?.(value);
    } else {
      // Uncontrolled component - manage internal state
      setInternalSelected(value);
      onChange?.(value);
    }
  };

  return (
    <div
      className={`segmented-picker-container ${className}`}
    >
      {options.map((option, index) => {
        const isSelected = currentSelected === option.value;
        const selectedIndex = options.findIndex((opt) => opt.value === currentSelected);
        const showDivider = index < options.length - 1 && 
                           selectedIndex !== index && 
                           selectedIndex !== index + 1;

        return (
          <button
            key={option.value + index}
            className={`segmented-picker-option ${
              isSelected
                ? "segmented-picker-option-selected"
                : "segmented-picker-option-unselected"
            }`}
            onClick={() => handleSelect(option.value)}
            aria-pressed={isSelected}
            type="button"
          >
            {option.label}
            {showDivider && (
              <span className="segmented-picker-divider" />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedPicker;